import { ref, type Ref } from "vue";
import { BaseService, ErrorResponse } from "../base";
import type { BetriebsProzess, Gebaeude, Grundstueck, Kontakt, LogItem, Standort, ZweckDerAnlage, Bescheid, AnlagenTeil, Raum, Sammelstelle, Abfallbehaelter, Auflage, Abfallrechtsregister, RankingUmweltaspekte, Anhang, Dekarbonisierungsstrategie, Umweltpolitik } from "./model";


export const StandortServiceSymbol = Symbol();

export interface IStandortService {
  Grunddaten: Ref<Standort>;
  Kontakte: Ref<Kontakt[]>;
  Grundstuecke: Ref<Grundstueck[]>;
  Gebaeude: Ref<Gebaeude[]>;
  Raeume: Ref<Raum[]>;
  Sammelstellen: Ref<Sammelstelle[]>;
  Abfallbehaelter: Ref<Abfallbehaelter[]>;
  Bescheide: Ref<Bescheid[]>;
  ZweckeDerAnlage: Ref<ZweckDerAnlage[]>;
  BetriebsProzesse: Ref<BetriebsProzess[]>;
  AnlagenTeile: Ref<AnlagenTeil[]>;
  Auflagen: Ref<Auflage[]>;
  Abfallrechtsregister: Ref<Abfallrechtsregister[]>;
  RankingUmweltaspekte: Ref<RankingUmweltaspekte>;
  Umweltpolitik: Ref<Umweltpolitik>;
  Anhaenge: Ref<Anhang[]>;

  LoadGrunddaten(): Promise<void>;
  SaveGrunddaten(subkind?: string): Promise<void>;

  Protokoll(): Promise<LogItem[]>;

  LoadKontakte(): Promise<void>;
  DeleteKontakt(kontaktId: string): Promise<void>;
  UpsertKontakt(kontakt: Kontakt): Promise<void>;

  LoadGrundstuecke(): Promise<void>;
  DeleteGrundstueck(grundstueckId: string): Promise<void>;
  UpsertGrundstueck(grundstueck: Grundstueck): Promise<void>;

  LoadGebaeude(): Promise<void>;
  DeleteGebaeude(gebaeudeId: string): Promise<void>;
  UpsertGebaeude(Gebaeude: Gebaeude): Promise<void>;

  LoadRaeume(): Promise<void>;
  DeleteRaum(raumId: string): Promise<void>;
  UpsertRaum(raum: Raum): Promise<void>;

  LoadSammelstellen(): Promise<void>;
  DeleteSammelstelle(sammelstelleId: string): Promise<void>;
  UpsertSammelstelle(sammelstelle: Sammelstelle): Promise<void>;

  LoadAbfallbehaelter(): Promise<void>;
  DeleteAbfallbehaelter(abfallbehaelterId: string): Promise<void>;
  UpsertAbfallbehaelter(abfallbehaelter: Abfallbehaelter): Promise<void>;

  LoadBetriebsProzesse(): Promise<void>;
  DeleteBetriebsProzess(betriebsProzessId: string): Promise<void>;
  UpsertBetriebsProzess(betriebsProzess: BetriebsProzess): Promise<void>;

  LoadZweckDerAnlage(): Promise<void>;
  DeleteZweckDerAnlage(zweckDerAnlageId: string): Promise<void>;
  UpsertZweckDerAnlage(zweckDerAnlage: ZweckDerAnlage): Promise<void>;

  LoadAnlagenTeile(): Promise<void>;
  DeleteAnlagenTeil(anlagenTeilId: string): Promise<void>;
  UpsertAnlagenTeil(anlagenTeil: AnlagenTeil): Promise<void>;

  LoadBescheide(): Promise<void>;
  DeleteBescheid(bescheidId: string): Promise<void>;
  UpsertBescheid(bescheid: Bescheid): Promise<void>;

  LoadAuflagen(): Promise<void>;
  DeleteAuflage(auflageId: string): Promise<void>;
  UpsertAuflage(auflage: Auflage): Promise<void>;

  LoadAbfallrechtsregister(): Promise<void>;
  DeleteAbfallrechtsregister(abfallrechtsregisterId: string): Promise<void>;
  UpsertAbfallrechtsregister(abfallrechtsregister: Abfallrechtsregister): Promise<void>;

  LoadRankingUmweltaspekte(): Promise<void>;
  DeleteRankingUmweltaspekte(rankingUmweltaspekteId: string): Promise<void>;
  UpsertRankingUmweltaspekte(rankingUmweltaspekte: RankingUmweltaspekte): Promise<void>;

  LoadUmweltpolitik(): Promise<void>;
  DeleteUmweltpolitik(umweltpolitikId: string): Promise<void>;
  UpsertUmweltpolitik(umweltpolitik: Umweltpolitik): Promise<void>;

  LoadDekarbonisierungstrategien(): Promise<void>;
  DeleteDekarbonisierungstrategie(dekarbonisierungstrategieId: string): Promise<void>;
  UpsertDekarbonisierungstrategie(dekarbonisierungstrategie: Dekarbonisierungsstrategie): Promise<void>;

  LoadAnhaenge(): Promise<void>;
  DeleteAnhang(anhangId: string): Promise<void>;
  UpsertAnhang(anhang: Anhang): Promise<void>;
}

export class StandortService extends BaseService implements IStandortService {
  private _standort: Ref<Standort>;
  private _kontakte: Ref<Kontakt[]>;
  private _grundstuecke: Ref<Grundstueck[]>;
  private _gebaeude: Ref<Gebaeude[]>;
  private _raeume: Ref<Raum[]>;
  private _sammelstellen: Ref<Sammelstelle[]>;
  private _abfallbehaelter: Ref<Abfallbehaelter[]>;
  private _bescheide: Ref<Bescheid[]>;
  private _betriebsProzesse: Ref<BetriebsProzess[]>;
  private _anlagenTeile: Ref<AnlagenTeil[]>;
  private _zweckDerAnlage: Ref<ZweckDerAnlage[]>;
  private _auflagen: Ref<Auflage[]>;
  private _abfallrechtsregister: Ref<Abfallrechtsregister[]>;
  private _rankingUmweltaspekte: Ref<RankingUmweltaspekte>;
  private _anhaenge: Ref<Anhang[]>;
  private _dekarbonisierungsstrategie: Ref<Dekarbonisierungsstrategie>;
  private _umweltpolitik: Ref<Umweltpolitik>;

  get Grunddaten(): Ref<Standort> {
    return this._standort;
  }

  get Kontakte(): Ref<Kontakt[]> {
    return this._kontakte;
  }

  get Grundstuecke(): Ref<Grundstueck[]> {
    return this._grundstuecke;
  }

  get Gebaeude(): Ref<Gebaeude[]> {
    return this._gebaeude;
  }

  get Raeume(): Ref<Raum[]> {
    return this._raeume;
  }

  get Sammelstellen(): Ref<Sammelstelle[]> {
    return this._sammelstellen;
  }

  get Abfallbehaelter(): Ref<Abfallbehaelter[]> {
    return this._abfallbehaelter;
  }

  get Bescheide(): Ref<Bescheid[]> {
    return this._bescheide;
  }

  get ZweckeDerAnlage(): Ref<ZweckDerAnlage[]> {
    return this._zweckDerAnlage;
  }

  get BetriebsProzesse(): Ref<BetriebsProzess[]> {
    return this._betriebsProzesse;
  }

  get AnlagenTeile(): Ref<AnlagenTeil[]> {
    return this._anlagenTeile;
  }

  get Auflagen(): Ref<Auflage[]> {
    return this._auflagen;
  }

  get Abfallrechtsregister(): Ref<Abfallrechtsregister[]> {
    return this._abfallrechtsregister;
  }

  get RankingUmweltaspekte(): Ref<RankingUmweltaspekte> {
    return this._rankingUmweltaspekte;
  }

  get Umweltpolitik(): Ref<Umweltpolitik> {
    return this._umweltpolitik;
  }

  get Anhaenge(): Ref<Anhang[]> {
    return this._anhaenge;
  }

  get Dekarbonisierungstrategien(): Ref<Dekarbonisierungsstrategie> {
    return this._dekarbonisierungsstrategie;
  }

  constructor(baseUri: string, token: string) {
    super(baseUri, token);
    this._standort = ref<Standort>({});
    this._kontakte = ref<Kontakt[]>([]);
    this._grundstuecke = ref<Grundstueck[]>([]);
    this._gebaeude = ref<Gebaeude[]>([]);
    this._raeume = ref<Raum[]>([]);
    this._sammelstellen = ref<Sammelstelle[]>([]);
    this._abfallbehaelter = ref<Abfallbehaelter[]>([]);
    this._bescheide = ref<Bescheid[]>([]);
    this._betriebsProzesse = ref<BetriebsProzess[]>([]);
    this._anlagenTeile = ref<AnlagenTeil[]>([]);
    this._zweckDerAnlage = ref<ZweckDerAnlage[]>([]);
    this._auflagen = ref<Auflage[]>([]);
    this._abfallrechtsregister = ref<Abfallrechtsregister[]>([]);
    this._rankingUmweltaspekte = ref<RankingUmweltaspekte>({});
    this._umweltpolitik = ref<Umweltpolitik>({});
    this._anhaenge = ref<Anhang[]>([]);
    this._dekarbonisierungsstrategie = ref<Dekarbonisierungsstrategie>({});
  }

  async Protokoll(): Promise<LogItem[]> {
    return await this.Get<LogItem[]>('protokoll', true) as LogItem[];
  }
  

  async LoadKontakte(): Promise<void> {
    this._kontakte.value = await this.Get<Kontakt[]>('kontakte', true) as Kontakt[];
  }
  async DeleteKontakt(kontaktId: string): Promise<void> {
    await this.Delete(`kontakte/${kontaktId}`);
  }
  async UpsertKontakt(kontakt: Kontakt): Promise<void> {
    await this.Patch('kontakte', kontakt);
  }

  async LoadGebaeude(): Promise<void> {
    this._gebaeude.value = await this.Get<Gebaeude[]>('gebaeude', true) as Gebaeude[];
  }
  async DeleteGebaeude(gebaeudeId: string): Promise<void> {
    await this.Delete(`gebaeude/${gebaeudeId}`);
  }
  async UpsertGebaeude(gebaeude: Gebaeude): Promise<void> {
    await this.Patch('gebaeude', gebaeude);
  }

  async LoadRaeume(): Promise<void> {
    this._raeume.value = await this.Get<Raum[]>('raeume', true) as Raum[];
  }
  async DeleteRaum(raumId: string): Promise<void> {
    await this.Delete(`raeume/${raumId}`);
  }
  async UpsertRaum(raum: Raum): Promise<void> {
    await this.Patch('raeume', raum);
  }

  async LoadSammelstellen(): Promise<void> {
    this._sammelstellen.value = await this.Get<Sammelstelle[]>('sammelstellen', true) as Sammelstelle[];
  }
  async DeleteSammelstelle(sammelstelleId: string): Promise<void> {
    await this.Delete(`sammelstellen/${sammelstelleId}`);
  }
  async UpsertSammelstelle(sammelstelle: Sammelstelle): Promise<void> {
    await this.Patch('sammelstellen', sammelstelle);
  }

  async LoadAbfallbehaelter(): Promise<void> {
    this._abfallbehaelter.value = await this.Get<Abfallbehaelter[]>('abfallbehaelter', true) as Abfallbehaelter[];
  }
  async DeleteAbfallbehaelter(abfallbehaelterId: string): Promise<void> {
    await this.Delete(`abfallbehaelter/${abfallbehaelterId}`);
  }
  async UpsertAbfallbehaelter(abfallbehaelter: Abfallbehaelter): Promise<void> {
    await this.Patch('abfallbehaelter', abfallbehaelter);
  }

  async LoadGrundstuecke(): Promise<void> {
    this._grundstuecke.value = await this.Get<Grundstueck[]>('grundstuecke', true) as Grundstueck[];
  }
  async DeleteGrundstueck(grundstueckId: string): Promise<void> {
    await this.Delete(`grundstuecke/${grundstueckId}`);
  }
  async UpsertGrundstueck(grundstueck: Grundstueck): Promise<void> {
    await this.Patch('grundstuecke', grundstueck);
  }

  async LoadZweckDerAnlage(): Promise<void> {
    this._zweckDerAnlage.value = await this.Get<ZweckDerAnlage[]>('zweckderanlage', true) as ZweckDerAnlage[];
  }
  async DeleteZweckDerAnlage(zweckDerAnlageId: string): Promise<void> {
    await this.Delete(`zweckderanlage/${zweckDerAnlageId}`);
  }
  async UpsertZweckDerAnlage(zweckDerAnlage: ZweckDerAnlage): Promise<void> {
    await this.Patch('zweckderanlage', zweckDerAnlage);
  }

  async LoadBetriebsProzesse(): Promise<void> {
    this._betriebsProzesse.value = await this.Get<BetriebsProzess[]>('betriebsprozesse', true) as BetriebsProzess[];
  }
  async DeleteBetriebsProzess(betriebsProzessId: string): Promise<void> {
    await this.Delete(`betriebsprozesse/${betriebsProzessId}`);
  }
  async UpsertBetriebsProzess(betriebsProzess: BetriebsProzess): Promise<void> {
    await this.Patch('betriebsprozesse', betriebsProzess);
  }

  async LoadBescheide(): Promise<void> {
    this._bescheide.value = await this.Get<Bescheid[]>('bescheide', true) as Bescheid[];
  }
  async DeleteBescheid(bescheidId: string): Promise<void> {
    await this.Delete(`bescheide/${bescheidId}`);
  }
  async UpsertBescheid(bescheid: Bescheid): Promise<void> {
    await this.Patch('bescheide', bescheid);
  }

  async LoadAnlagenTeile(): Promise<void> {
    this._anlagenTeile.value = await this.Get<AnlagenTeil[]>('anlagenteile', true) as AnlagenTeil[];
  }
  async DeleteAnlagenTeil(anlagenteilId: string): Promise<void> {
    await this.Delete(`anlagenteile/${anlagenteilId}`);
  }
  async UpsertAnlagenTeil(anlagenTeil: AnlagenTeil): Promise<void> {
    await this.Patch('anlagenteile', anlagenTeil);
  }

  async LoadAuflagen(): Promise<void> {
    this._auflagen.value = await this.Get<Auflage[]>('auflagen', true) as AnlagenTeil[];
  }
  async DeleteAuflage(auflageId: string): Promise<void> {
    await this.Delete(`auflagen/${auflageId}`);
  }
  async UpsertAuflage(auflage: Auflage): Promise<void> {
    await this.Patch('auflagen', auflage);
  }

  async LoadAbfallrechtsregister(): Promise<void> {
    this._abfallrechtsregister.value = await this.Get<Abfallrechtsregister[]>('abfallrechtsregister', true) as Abfallrechtsregister[];
  }
  async DeleteAbfallrechtsregister(auflageId: string): Promise<void> {
    await this.Delete(`abfallrechtsregister/${auflageId}`);
  }
  async UpsertAbfallrechtsregister(abfallrechtsregister: Abfallrechtsregister): Promise<void> {
    await this.Patch('abfallrechtsregister', abfallrechtsregister);
  }

  async LoadRankingUmweltaspekte(): Promise<void> {
    var results = await this.Get<RankingUmweltaspekte[]>('rankingUmweltaspekte', true) as RankingUmweltaspekte[];
    
    if(results.length != 1)
      return;

    this._rankingUmweltaspekte.value = results[0];
  }
  async DeleteRankingUmweltaspekte(rankingUmweltaspekteId: string): Promise<void> {
    await this.Delete(`rankingUmweltaspekte/${rankingUmweltaspekteId}`);
  }
  async UpsertRankingUmweltaspekte(rankingUmweltaspekte: RankingUmweltaspekte): Promise<void> {
    await this.Patch('rankingUmweltaspekte', rankingUmweltaspekte);
  }

  async LoadAnhaenge(): Promise<void> {
    this._anhaenge.value = await this.Get<Anhang[]>('anhaenge', true) as Anhang[];
  }
  async DeleteAnhang(anhangId: string): Promise<void> {
    await this.Delete(`anhaenge/${anhangId}`);
  }
  async UpsertAnhang(anhang: Anhang): Promise<void> {
    await this.Patch('anhaenge', anhang);
  }

  async LoadDekarbonisierungstrategien(): Promise<void> {
    var results = await this.Get<Dekarbonisierungsstrategie[]>('dekarbonisierungsstrategien', true) as Dekarbonisierungsstrategie[];
    
    if(results.length != 1)
      return;

    this._dekarbonisierungsstrategie.value = results[0];
  }
  async DeleteDekarbonisierungstrategie(dekarbonisierungstrategieId: string): Promise<void> {
    await this.Delete(`dekarbonisierungsstrategien/${dekarbonisierungstrategieId}`);
  } 
  async UpsertDekarbonisierungstrategie(dekarbonisierungstrategie: Dekarbonisierungsstrategie): Promise<void> {
    await this.Patch('dekarbonisierungsstrategien', dekarbonisierungstrategie);
  }

  async LoadUmweltpolitik(): Promise<void> {
    var results = await this.Get<Umweltpolitik[]>('umweltpolitik', true) as Umweltpolitik[];
    
    if(results.length == 0)
      return;

    this._umweltpolitik.value = results[0];
  }
  async DeleteUmweltpolitik(umweltpolitikId: string): Promise<void> {
    await this.Delete(`umweltpolitik/${umweltpolitikId}`);
  }
  async UpsertUmweltpolitik(umweltpolitik: Umweltpolitik): Promise<void> {
    await this.Patch('umweltpolitik', umweltpolitik);
    await this.LoadUmweltpolitik();
  }

  async LoadGrunddaten(): Promise<void> {
    this._standort.value = await this.Get<Standort>('', true) as Standort;
  }

  async SaveGrunddaten(subkind?: string): Promise<void> {
    this._standort.value.lastModifiedSubkind = subkind;
    await this.Patch<Standort>('', this._standort.value)
  }
}